import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert, Button, DiscordIcon, FormField, Input } from '@playerbase/ui';
import { SignUpEmailFormSchema } from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { getSiteUrl } from '../lib/site-url.js';
import { useSharedEmail } from '../lib/use-shared-email.js';
import { AuthShell } from './auth-shell.js';

type Mode = 'form' | 'sent';

// Seconds the user must wait between resend clicks. Reset to this value when
// they land in 'sent' mode and after each successful resend.
const RESEND_COOLDOWN_SECONDS = 10;

export function SignUpPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  // Threaded through from /invite/:token so we can attach the token to the
  // new user's auth metadata. That keeps the redemption working when the
  // verification link is opened in a different browser context (where
  // localStorage from /invite is gone).
  const inviteToken = searchParams.get('invite');
  const [mode, setMode] = useState<Mode>('form');
  const [email, setEmail] = useSharedEmail();
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [serverError, setServerError] = useState('');
  const [oauthError, setOauthError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (mode !== 'sent') return;
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => { window.clearInterval(id); };
  }, [mode, resendCooldown]);

  async function handleDiscordSignUp(): Promise<void> {
    setOauthError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        scopes: 'identify email',
        redirectTo: `${getSiteUrl()}/auth/callback`,
      },
    });
    if (error) {
      setOauthError(error.message);
    }
  }


  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setEmailError('');
    setPasswordError('');
    setServerError('');

    const result = SignUpEmailFormSchema.safeParse({ email, password });
    if (!result.success) {
      for (const issue of result.error.issues) {
        if (issue.path[0] === 'email') setEmailError(issue.message);
        if (issue.path[0] === 'password') setPasswordError(issue.message);
      }
      return;
    }

    setSubmitting(true);
    try {
      // Compose options separately so the optional `data` field is omitted
      // entirely when there's no invite token, instead of being set to
      // undefined (which exactOptionalPropertyTypes forbids).
      const signUpOptions: { emailRedirectTo: string; data?: { invite_token: string } } = {
        emailRedirectTo: `${getSiteUrl()}/auth/callback`,
      };
      if (inviteToken !== null) {
        signUpOptions.data = { invite_token: inviteToken };
      }
      const { error } = await supabase.auth.signUp({
        email: result.data.email,
        password: result.data.password,
        options: signUpOptions,
      });
      if (error) {
        setServerError(error.message);
      } else {
        setMode('sent');
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        // Marker for AuthCallbackPage to detect a same-browser confirmation.
        // The resend email uses Supabase's implicit flow which works cross-
        // browser by design; we use this to enforce the same "log in below"
        // UX as the PKCE flow when the link is opened elsewhere.
        try {
          localStorage.setItem('playerbase.auth.signup-origin', result.data.email);
        } catch {
          // localStorage may be unavailable
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${getSiteUrl()}/auth/callback`,
        },
      });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } finally {
      setResending(false);
    }
  }

  if (mode === 'sent') {
    return (
      <AuthShell title="Check your inbox" subtitle="We've sent a confirmation link to your email address.">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-muted">
            Sent to <span className="font-medium text-text">{email}</span>. Click the link in the email to complete sign-up.
          </p>
          <Button
            variant="ghost"
            onClick={() => { void handleResend(); }}
            disabled={resending || resendCooldown > 0}
          >
            {resending
              ? 'Sending…'
              : resendCooldown > 0
                ? `Resend email in ${resendCooldown}s`
                : 'Resend email'}
          </Button>
          <button
            type="button"
            className="text-sm text-text-muted underline underline-offset-2 hover:text-text transition-colors"
            onClick={() => setMode('form')}
          >
            Back to sign-up
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Sign up" subtitle="Create your studio account">
      <div className="flex flex-col gap-6">
        {/* Discord OAuth */}
        <div className="flex flex-col gap-3">
          <Button
            variant="discord"
            className="w-full"
            onClick={() => { void handleDiscordSignUp(); }}
          >
            <DiscordIcon className="h-4 w-auto" />
            Sign up with Discord
          </Button>
          {oauthError !== '' && (
            <Alert tone="danger">{oauthError}</Alert>
          )}
        </div>

        {/* Divider */}
        <div className="relative flex items-center gap-3">
          <div className="flex-1 border-t border-border" />
          <span className="text-xs text-text-muted">or</span>
          <div className="flex-1 border-t border-border" />
        </div>

        {/* Email form */}
        <form onSubmit={(e) => { void handleEmailSubmit(e); }} className="flex flex-col gap-4" noValidate>
          {serverError !== '' && (
            <Alert tone="danger">{serverError}</Alert>
          )}

          <FormField error={emailError !== '' ? emailError : undefined}>
            <FormField.Label>Email</FormField.Label>
            <FormField.Control>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </FormField.Control>
            {emailError !== '' && <FormField.ErrorText>{emailError}</FormField.ErrorText>}
          </FormField>

          <FormField error={passwordError !== '' ? passwordError : undefined}>
            <FormField.Label>Password</FormField.Label>
            <FormField.Control>
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
              />
            </FormField.Control>
            {passwordError !== '' && <FormField.ErrorText>{passwordError}</FormField.ErrorText>}
          </FormField>

          <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-text-muted">
          Already have an account?{' '}
          <Link to="/sign-in" className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity">
            Log in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
